import { useEffect, useMemo, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { RouterCombobox } from "./RouterCombobox";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export function GantiVoucherDialog({ open, onClose, channel, suggestions = [] }) {
  const { routers } = useRouters();
  const [routerId, setRouterId] = useState("");
  const [pkgs, setPkgs] = useState([]);
  const [form, setForm] = useState({ profile: "", count: 1, sisa_hari: 0, sisa_jam: 0, limit_gb: "", note: "" });
  const [busy, setBusy] = useState(false);
  const sorted = useMemo(() => [...routers].sort((a, b) => a.name.localeCompare(b.name)), [routers]);
  useEffect(() => {
    if (!routerId) { setPkgs([]); return; }
    api.get(`/routers/${routerId}/voucher-packages`).then((r) => setPkgs(r.data || [])).catch(() => setPkgs([]));
  }, [routerId]);
  const pickProfile = (p) => {
    const pk = pkgs.find((x) => x.profile === p);
    setForm({ ...form, profile: p, count: Math.max(1, pk?.codes_per_set || 1) });
  };
  const submit = async () => {
    if (!routerId || !form.profile) return toast.error("Pilih cabang dan profile");
    setBusy(true);
    try {
      const { data } = await api.post("/vouchers/ganti-komplain", { channel, router_id: routerId, profile: form.profile, count: Number(form.count) || 1, sisa_hari: Number(form.sisa_hari) || 0, sisa_jam: Number(form.sisa_jam) || 0, limit_gb: Number(form.limit_gb) || 0, old_codes: "", note: form.note });
      toast.success(`${data.codes.length} kode pengganti dibuat & dikirim ke chat agen`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="ganti-voucher-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><RefreshCcw className="w-5 h-5 text-[#F04E23]" /> Ganti Voucher (Komplain)</DialogTitle>
          <DialogDescription>Buat kode pengganti <b>gratis</b> (saldo agen tidak dipotong) dengan sisa masa aktif tertentu. Kode dikirim otomatis ke chat agen ini; kode lama dinonaktifkan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Cabang</Label><RouterCombobox routers={sorted} value={routerId || "ALL"} onChange={(v) => setRouterId(v === "ALL" ? "" : v)} allLabel="Pilih cabang..." testid="gv-router" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Profile</Label>
              <Select value={form.profile} onValueChange={pickProfile} disabled={!pkgs.length}>
                <SelectTrigger data-testid="gv-profile"><SelectValue placeholder={routerId ? (pkgs.length ? "Pilih profile" : "Tidak ada paket") : "Pilih cabang dulu"} /></SelectTrigger>
                <SelectContent>{pkgs.map((p) => <SelectItem key={p.profile} value={p.profile} data-testid={`gv-profile-${p.profile}`}>{p.profile}{p.codes_per_set > 1 ? ` (${p.codes_per_set} HP)` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Jumlah kode</Label><Input type="number" min="1" max="200" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} data-testid="gv-count" />{Number(form.count) > 200 && <p className="text-[10px] text-rose-400" data-testid="gv-count-err">Maksimal 200 kode per penggantian</p>}</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5"><Label>Sisa hari</Label><Input type="number" min="0" max="90" value={form.sisa_hari} onChange={(e) => setForm({ ...form, sisa_hari: e.target.value })} data-testid="gv-sisa-hari" /></div>
            <div className="space-y-1.5"><Label>Sisa jam</Label><Input type="number" min="0" max="23" value={form.sisa_jam} onChange={(e) => setForm({ ...form, sisa_jam: e.target.value })} data-testid="gv-sisa-jam" /></div>
            <div className="space-y-1.5"><Label>Kuota GB</Label><Input type="number" min="0" step="0.5" placeholder="unlimited" value={form.limit_gb} onChange={(e) => setForm({ ...form, limit_gb: e.target.value })} data-testid="gv-gb" /></div>
          </div>
          <p className="text-[11px] text-muted-foreground">Sisa hari/jam dipasang sebagai <span className="font-mono">limit-uptime</span> di router (0 = ikuti paket). Contoh komplain 4HP-30HARI dipakai 12 hari → isi 18 hari, 4 kode.</p>
          <div className="space-y-1.5"><Label>Catatan untuk agen</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="opsional" data-testid="gv-note" /></div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={onClose} data-testid="gv-cancel">Batal</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#F04E23] hover:bg-[#d8431c] text-white" data-testid="gv-submit">{busy ? "Membuat..." : `Buat ${form.count || 1} Kode & Kirim`}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
